import ClassNames from 'classnames'
import * as React from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import I18n from '../../core/i18n'
import * as constants from '../../static/constants'
import { IsMobile, IsTabletOrDesktop, IsMobileOrTablet, IsDesktop } from '../../utils/responsive'
import HeaderMenu from '../atoms/HeaderMenu'
import HeaderNotifications from 'components/atoms/HeaderNotifications'
import { IUser } from '../../static/interfaces'
import { HeaderSearchPanel } from 'components/atoms/HeaderSearchPanel'
import { IJsonResponse } from 'core/JsonApiSerializer'
import { MobileSearchParent } from './MobileSearchParent'

interface IWindow {
  localePath: { [key: string]: string }
  location: any
  innerWidth: number
}
declare var window: IWindow

interface IProps {
  isSignedIn: boolean
  user: IUser
  agentMember?: { name: string; email: string }
  category_features?: IJsonResponse
  area_features?: IJsonResponse
  lang?: { [key: string]: string }
  top?: boolean
  current_feature?: { slug: string; feature_type: string } | null
}

const ENTER_KEYCODE = 13
const DATASIGN_CMP_HOST_ID = 'datasign_cmp__host'
const HAS_DATASIGN_CMP_CLASS = 'has-datasign-cmp'

const Header: React.FC<IProps> = props => {
  const [searchBoxIsOpen, setSearchBoxIsOpen] = React.useState(false)
  const [isScrolled, setIsScrolled] = React.useState(false) // スクロール状態を追跡する状態を追加
  const { isSignedIn, user } = props

  // スクロール位置を検出するための効果
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = document.documentElement.scrollTop
      if (scrollPosition > 20 && !isScrolled) {
        setIsScrolled(true)
      } else if (scrollPosition <= 20 && isScrolled) {
        setIsScrolled(false)
      }
    }

    document.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [isScrolled])

  React.useEffect(() => {
    if (props.lang) {
      window.localePath = { ...props.lang }
    }
  }, [])

  React.useEffect(() => {
    if (document.body.classList.contains('is-loading')) {
      const loading = document.getElementById('header-ls')
      loading.style.display = 'none'
      document.body.classList.remove('is-loading')
    }
  })

  React.useEffect(() => {
    const updateDatasignCmpClass = () => {
      const hasDatasignCmp = Boolean(document.getElementById(DATASIGN_CMP_HOST_ID))
      document.body.classList.toggle(HAS_DATASIGN_CMP_CLASS, hasDatasignCmp)

      return hasDatasignCmp
    }

    if (updateDatasignCmpClass()) {
      return () => {
        document.body.classList.remove(HAS_DATASIGN_CMP_CLASS)
      }
    }

    const observer = new MutationObserver(() => {
      if (updateDatasignCmpClass()) {
        observer.disconnect()
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      document.body.classList.remove(HAS_DATASIGN_CMP_CLASS)
    }
  }, [])

  const isUnderTablet = matchMedia(`(max-width: ${constants.BREAKPOINT_TABLET_LARGE}px)`).matches

  return (
    <HeaderWrapper className={ClassNames('Header', { top: props.top, scrolled: isScrolled })}>
      <GlobalStyle top={props.top} />
      <div className="Header_Inner">
        <a className="Logo" href={user?.admin ? '/admin/posts' : '/'}>
          <IsTabletOrDesktop>
            <img
              src={`${constants.ASSET_HOST}/images/logo-vertical2.svg`}
              alt={I18n.t('admin.settings.default_site')}
              className="LogoFull"
            />
          </IsTabletOrDesktop>
          <IsMobile>
            <img
              src={`${constants.ASSET_HOST}/images/logo_symbol.svg`}
              alt={I18n.t('admin.settings.default_site')}
              className="LogoSymbol"
            />
          </IsMobile>
        </a>

        <IsTabletOrDesktop>
          <HeaderSearchPanel
            areaFeatures={props.area_features}
            isUnderTablet={isUnderTablet}
            isScrolled={isScrolled}
            currentFeature={props.current_feature}
          />
        </IsTabletOrDesktop>
        <IsMobile>
          <MobileSearchParent
            searchBoxIsOpen={searchBoxIsOpen}
            areaFeatures={props.area_features}
            setSearchBoxIsOpen={setSearchBoxIsOpen}
            currentFeature={props.current_feature}
          />
        </IsMobile>
        {isSignedIn ? (
          <div className="Header_Items">
            <IsDesktop>
              <HeaderNotifications unreadNotifications={props.user.unread_notifications_count} />
              <HeaderMenu
                user={props.user}
                agentMember={props.agentMember}
                lang={props.lang}
                isSignedIn={isSignedIn}
                desktop={true}
              />
            </IsDesktop>
            <IsMobileOrTablet>
              <HeaderMenu
                user={props.user}
                agentMember={props.agentMember}
                lang={props.lang}
                isSignedIn={isSignedIn}
                mobile={true}
              />
            </IsMobileOrTablet>
          </div>
        ) : (
          <>
            <div className="Header_Items">
              <IsDesktop>
                <HeaderMenu
                  user={props.user}
                  lang={props.lang}
                  isSignedIn={isSignedIn}
                  desktop={true}
                />
              </IsDesktop>
              <IsMobileOrTablet>
                <HeaderMenu
                  user={props.user}
                  lang={props.lang}
                  isSignedIn={isSignedIn}
                  mobile={true}
                />
              </IsMobileOrTablet>
            </div>
          </>
        )}
      </div>
    </HeaderWrapper>
  )
}

const GlobalStyle = createGlobalStyle`
  main {
    min-height: calc(100vh - 48px - ${constants.HEADER_HEIGHT + constants.FOOTER_HEIGHT}px);
    //padding-top: ${constants.HEADER_HEIGHT}px;
    @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
      min-height: calc(100vh - 48px - ${constants.MOBILE_HEADER_HEIGHT +
        constants.FOOTER_HEIGHT}px);
      //padding-top: ${constants.MOBILE_HEADER_HEIGHT}px;
      background: ${constants.COLORS.white}
    }
  }

  body.${
    constants.HAS_FIXED_FOOTER_CLASS
  }.${HAS_DATASIGN_CMP_CLASS} [id^="datasign_cmp"]::part(floating-container-button) {
    bottom: 76px !important;
    left: 16px !important;
  }
`

const HeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  height: ${constants.SCROLLING_HEADER_HEIGHT}px;
  padding: 0px 100px;
  background-color: ${constants.COLORS.white};
  z-index: 5000;

  /* スクロール状態のときのみ影を表示 */
  &.scrolled {
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  }
  @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
    height: ${constants.MOBILE_HEADER_HEIGHT}px;
    padding: 0 50px;
  }

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL - 1}px) {
    padding: 0;
  }

  .SearchToggle {
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    .icon {
      width: 24px;
      height: 24px;
    }
  }

  &.top {
    height: ${constants.SCROLLING_HEADER_HEIGHT}px;
    .Header_Inner {
      display: flex;
      height: 100%;
    }
    @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
      height: ${constants.MOBILE_HEADER_HEIGHT}px;
    }
  }

  .Header_Inner {
    display: flex;
    height: ${constants.SCROLLING_HEADER_HEIGHT}px;
    align-items: center;
    justify-content: space-between;
    max-width: 1440px;
    margin: 0 auto;
    @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
      height: ${constants.MOBILE_HEADER_HEIGHT}px;
    }
  }

  .Header_Left {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .Logo {
    flex-shrink: 0;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL - 1}px) {
      margin-left: 16px;
    }

    > img {
      display: block;
      height: auto;
      &.LogoFull {
        width: 148px;
        height: 56px;
        @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
          width: 100px;
          height: auto;
        }
      }
      &.LogoSymbol {
        width: 44px;
      }
    }
  }

  .Header_Items {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
`
export default Header
